import React from 'react';
import { Box, Divider } from '@mui/material';
import { FieldValues, useWatch, useFormContext, Path, Controller, PathValue } from 'react-hook-form';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { FilterArrayValuesAutocomplete } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/FilterValuesAutocomplete';
import {
  FilterTypeToConceptMap,
  BeaconFilterFieldTypeSchema,
  FilterOperatorSchema,
  AtlasFilterFieldTypeSchema
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchema';
import {
  BeaconFilterFieldTypeType,
  FilterConfigType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';
import colors from 'src/utils/colors';
import { SINGLETON_CATEGORICAL_TYPES } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/utils';
import { getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector/types';
import { ControlledDropdown } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledDropdown';
import CloseIconButton from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/CloseIconButton';
import {
  useFormBrands,
  useFormBusinessUnits,
  useFormCampaigns,
  useFormCategories,
  useFormRetailers,
  useFormSegments,
  useFormSubcategories
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useCategoricalFilters';

type Props<T extends FieldValues> = {
  groupIndex: number;
  blockIndex: number;
};

const isNumericType = (type: BeaconFilterFieldTypeType): boolean =>
  ['Category', 'Subcategory', 'Brand', 'Retailer'].includes(type);

// Chip styles moved to shared `FilterChip` component

/**
 * TODO: This component is temporary. The AlertBuilderSchema will be updated with stricter types for this categorical filter.
 * Currently hardcoded the concept options and autocomplete options.
 */
function CategoricalFilterSection<T extends FieldValues>({ groupIndex, blockIndex }: Props<T>) {
  const { setValue, control } = useFormContext<T>();
  const formCategories = useFormCategories();
  const formSubcategories = useFormSubcategories();
  const formRetailers = useFormRetailers();
  const formBrands = useFormBrands();
  const formSegments = useFormSegments();
  const formBusinessUnits = useFormBusinessUnits();
  const formCampaigns = useFormCampaigns();

  const getItemsForCategoricalType = (
    categoricalType: BeaconFilterFieldTypeType
  ): Array<{ id: string; name: string }> => {
    switch (categoricalType) {
      case 'Brand':
        return formBrands;
      case 'Category':
        return formCategories;
      case 'Subcategory':
        return formSubcategories;
      case 'Retailer':
        return formRetailers;
      case 'Business Unit':
        return formBusinessUnits;
      case 'Segment':
        return formSegments;
      case 'Campaign':
        return formCampaigns;
      case 'RetailerSku':
        // Intentionally empty to allow freeSolo entries at scale
        return [];
      case 'Keyword':
        return [];
      default:
        return [];
    }
  };

  // Helper: return an item object with id/name based on hardcoded mapping (or free entry for string-id types)
  const buildItemForType = (type: BeaconFilterFieldTypeType, label: string): { id: string; name: string } => {
    const items = getItemsForCategoricalType(type);
    const found = items.find((i) => i.name === label);
    if (found) return found;
    // Allow free entry only for non-numeric types
    if (!isNumericType(type)) {
      return { id: label, name: label };
    }
    // Fallback for numeric types: default to first or 0
    return items[0] ?? { id: '0', name: label };
  };

  // Options shown in UI (names only), backed by HARDCODED_FILTER_OPTIONS id/name pairs
  const getAutocompleteOptions = (type: BeaconFilterFieldTypeType): Array<{ id: string; name: string }> => {
    const items = getItemsForCategoricalType(type);
    return items ?? [];
  };

  const getOptionsForFilter = (filter: FilterConfigType): Array<string | { id: string; name: string }> => {
    return getAutocompleteOptions(filter.type);
  };

  // Base paths for this rule block
  const basePath = `queryJSON.rules.ruleGroups.${groupIndex}.ruleBlocks.${blockIndex}` as Path<T>;
  const filtersPath = `${basePath}.categoricalfilters` as Path<T>;

  // Watch the categorical filters
  const filters = (useWatch({ name: filtersPath }) as FilterConfigType[]) || [];

  // Function to handle type change and reset values
  const handleTypeChange = (index: number, newType: BeaconFilterFieldTypeType) => {
    const typePath = `${filtersPath}.${index}.type` as Path<T>;
    const conceptPath = `${filtersPath}.${index}.concept` as Path<T>;
    const valuesPath = `${filtersPath}.${index}.values` as Path<T>;

    // Set the new type
    setValue(typePath, newType as PathValue<T, typeof typePath>);

    // Set the corresponding concept (schema map is source of truth)
    const concept = FilterTypeToConceptMap[newType];
    setValue(conceptPath, concept as PathValue<T, typeof conceptPath>);

    // Reset the values array regardless of operator
    setValue(valuesPath, [] as PathValue<T, typeof valuesPath>);
  };

  // Function to handle operator change and reset values
  const handleOperatorChange = (index: number, newOperator: string) => {
    const operatorPath = `${filtersPath}.${index}.operator` as Path<T>;
    const valuesPath = `${filtersPath}.${index}.values` as Path<T>;

    // Set the new operator
    setValue(operatorPath, newOperator as PathValue<T, typeof operatorPath>);

    // Reset the values array when operator changes
    setValue(valuesPath, [] as PathValue<T, typeof valuesPath>);
  };

  // Only show if there are filters
  if (filters.length === 0) {
    return null;
  }

  // Compute available type options for a specific row (allow its current value, disallow others already used)
  const getAvailableTypeOptions = (rowIndex: number, isAtlas: boolean): string[] => {
    const allOptions = isAtlas ? AtlasFilterFieldTypeSchema.options : BeaconFilterFieldTypeSchema.options;
    // If there's only one categorical filter, allow switching to any type
    if (filters.length <= 1) {
      return [...allOptions];
    }

    const selected = new Set<string>(filters.map((f) => f.type).filter(Boolean));
    const currentType = filters[rowIndex]?.type;
    if (currentType) selected.delete(currentType);

    const hasSingleton = filters.some((f) => SINGLETON_CATEGORICAL_TYPES.includes(f.type as BeaconFilterFieldTypeType));

    // If a non-singleton exists, hide singleton options from the list
    const hasNonSingleton = filters.some(
      (f) => f.type && !SINGLETON_CATEGORICAL_TYPES.includes(f.type as BeaconFilterFieldTypeType)
    );

    return allOptions.filter((t) => {
      if (selected.has(t)) return false;
      if (hasSingleton) {
        // When a singleton is already chosen, only allow that one to remain selectable for its own row
        return t === currentType; // prevent adding another type
      }
      if (hasNonSingleton) {
        // When any non-singleton is present, block singleton types from appearing in options for new rows
        if (SINGLETON_CATEGORICAL_TYPES.includes(t as BeaconFilterFieldTypeType)) return false;
      }
      return true;
    });
  };

  /**
   * In some cases like RetailerSku and Keyword, we allow arbitrary entries.
   * This happens when there could be 100K of retailer SKUs or 100K of keywords.
   * In these cases, we don't want to show the autocomplete options.
   */
  const isArbitraryEntry = (filter: FilterConfigType): boolean => {
    return getOptionsForFilter(filter).length === 0;
  };

  // Final options shown in the dropdown. In freeSolo mode, just return empty array
  // since the Controller will handle current values automatically
  const getDropdownOptions = (filter: FilterConfigType): Array<string | { id: string; name: string }> => {
    return getOptionsForFilter(filter);
  };

  const isAtlas = getAppName() === AppName.Atlas;

  return (
    <Box sx={{ marginTop: '16px' }}>
      {/* Divider */}
      <Divider sx={{ marginBottom: '16px', borderColor: colors.graysSecondary }} />

      {/* Section Title */}
      <Text variant="body1" sx={{ fontWeight: 600, marginBottom: '16px', display: 'block' }}>
        Categorical Filters
      </Text>

      {/* Filter rows */}
      {filters.map((filter, index) => (
        <Flex key={filtersPath + filter.type} gap={16} alignItems="center" sx={{ marginBottom: '12px' }}>
          {/* Type Dropdown */}
          <Box sx={{ flex: 1, maxWidth: '200px' }}>
            <Controller
              name={`${filtersPath}.${index}.type` as Path<T>}
              control={control}
              render={({ field: { value }, fieldState: { error } }) => (
                <ControlledDropdown
                  label=""
                  value={value || ''}
                  onChange={(newValue) => handleTypeChange(index, newValue as BeaconFilterFieldTypeType)}
                  options={getAvailableTypeOptions(index, isAtlas)}
                  placeholder="Select Filter Type"
                  error={!!error}
                />
              )}
            />
          </Box>

          {/* Operator Dropdown */}
          <Box sx={{ flex: 1, maxWidth: '60px' }}>
            <Controller
              name={`${filtersPath}.${index}.operator` as Path<T>}
              control={control}
              render={({ field: { value }, fieldState: { error } }) => (
                <ControlledDropdown
                  label=""
                  value={value || FilterOperatorSchema.enum['IN']}
                  onChange={(newValue) => handleOperatorChange(index, newValue)}
                  options={FilterOperatorSchema.options}
                  placeholder={FilterOperatorSchema.enum['IN']}
                  error={!!error}
                />
              )}
            />
          </Box>

          <FilterArrayValuesAutocomplete
            idBase={`${filtersPath}-${filter.type}`}
            options={getDropdownOptions(filter)}
            freeSolo={isArbitraryEntry(filter)}
            placeholder={isArbitraryEntry(filter) ? 'Enter to add values' : 'Select values'}
            name={`${filtersPath}.${index}.values` as Path<T>}
            buildItem={(label) => buildItemForType(filter.type, label)}
            fieldType={filter.type}
          />

          {/* Remove Button */}
          <Box sx={{ flex: 0, marginTop: '5px' }}>
            <CloseIconButton
              onClick={() => {
                const updatedFilters = filters.filter((_, i) => i !== index);
                setValue(filtersPath, updatedFilters as PathValue<T, typeof filtersPath>);
              }}
            />
          </Box>
        </Flex>
      ))}
    </Box>
  );
}

export default CategoricalFilterSection;
